import {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { IconButton, Tooltip, useTheme } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { io, Socket } from 'socket.io-client';
import {
  CHATBOT_CONFIG,
  ChatHistoryItem,
  buildRequest,
  errorMessageFor,
  parseResponse
} from './config';
import './ArkChatbot.css';

interface ChatMessage {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  buttons?: string[];
  time: number;
  isError?: boolean;
}

interface StoredConversation {
  sessionId: string;
  startedAt?: number;
  messages: ChatMessage[];
}

const newId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const welcomeMessages = (): ChatMessage[] => {
  const now = Date.now();
  const messages: ChatMessage[] = CHATBOT_CONFIG.welcomeMessages.map((text) => ({
    id: newId(),
    sender: 'bot',
    text,
    time: now
  }));
  return messages;
};

const freshConversation = (): StoredConversation => ({
  sessionId: newId(),
  startedAt: Date.now(),
  messages: welcomeMessages()
});

const storageKey = () =>
  `${CHATBOT_CONFIG.storageKey}:${localStorage.getItem('userId') ?? 'anon'}`;

const loadConversation = (): StoredConversation => {
  if (!CHATBOT_CONFIG.persist) return freshConversation();
  try {
    const raw = localStorage.getItem(storageKey());
    const parsed = raw ? (JSON.parse(raw) as StoredConversation) : null;
    // A saved chat the user never asked anything in (e.g. only old welcome
    // bubbles) opens on the greeting instead.
    if (
      parsed?.sessionId &&
      Array.isArray(parsed.messages) &&
      parsed.messages.some((m) => m.sender === 'user')
    ) {
      return parsed;
    }
  } catch {
    // Storage unavailable or corrupt — start fresh.
  }
  return freshConversation();
};

// First name for the greeting: "shaheryar@inabia.com" / "shaheryar khan" -> "Shaheryar".
const greetingName = () => {
  const raw = (
    localStorage.getItem('username') ||
    localStorage.getItem('email') ||
    ''
  )
    .split('@')[0]
    .split(/[\s._-]+/)[0];
  return raw ? raw[0].toUpperCase() + raw.slice(1) : '';
};

// Links in bot replies open in a new tab so the user doesn't lose the app.
const markdownRenderer = new marked.Renderer();
markdownRenderer.link = (href, title, text) =>
  `<a href="${href ?? ''}"${
    title ? ` title="${title}"` : ''
  } target="_blank" rel="noopener noreferrer">${text}</a>`;

const renderMarkdown = (text: string) =>
  DOMPurify.sanitize(
    marked.parse(text ?? '', { renderer: markdownRenderer, breaks: true }),
    { ADD_ATTR: ['target'] }
  );

// "03:47 PM, September 24"
const formatMeta = (time: number) => {
  const d = new Date(time);
  const clock = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  const day = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  return `${clock}, ${day}`;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Types each topic out, pauses, deletes it and moves on to the next.
const useTypewriter = (words: string[]) => {
  const [text, setText] = useState(() =>
    prefersReducedMotion() ? words[0] ?? '' : ''
  );

  useEffect(() => {
    if (!words.length) return;
    const reduced = prefersReducedMotion();
    let word = 0;
    let chars = reduced ? words[0].length : 0;
    let deleting = false;
    let timer: number;

    const tick = () => {
      const current = words[word];
      if (reduced) {
        word = (word + 1) % words.length;
        setText(words[word]);
        timer = window.setTimeout(tick, 3000);
        return;
      }
      if (!deleting) {
        chars += 1;
        setText(current.slice(0, chars));
        if (chars === current.length) {
          deleting = true;
          timer = window.setTimeout(tick, 1800);
          return;
        }
        timer = window.setTimeout(tick, 55);
      } else {
        chars -= 1;
        setText(current.slice(0, chars));
        if (chars === 0) {
          deleting = false;
          word = (word + 1) % words.length;
          timer = window.setTimeout(tick, 350);
          return;
        }
        timer = window.setTimeout(tick, 25);
      }
    };
    timer = window.setTimeout(tick, reduced ? 3000 : 400);
    return () => window.clearTimeout(timer);
  }, [words]);

  return text;
};

// Robot in the chat header.
const Avatar = ({ size }: { size: 'md' }) => {
  const src = CHATBOT_CONFIG.avatarUrl;
  return (
    <span
      className={`ark-chatbot__avatar ark-chatbot__avatar--${size}${
        src ? '' : ' ark-chatbot__avatar--initial'
      }`}
    >
      {src ? (
        <img src={src} alt="" draggable={false} />
      ) : (
        <span className="ark-chatbot__avatar-initial">
          {(CHATBOT_CONFIG.title.trim()[0] ?? 'A').toUpperCase()}
        </span>
      )}
    </span>
  );
};

const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.9L12 2.5z" />
    <path d="M19 15l.8 2.2 2.2.8-2.2.8L19 21l-.8-2.2-2.2-.8 2.2-.8L19 15z" />
  </svg>
);

const Greeting = ({
  onPick,
  disabled
}: {
  onPick: (text: string) => void;
  disabled: boolean;
}) => {
  const topic = useTypewriter(CHATBOT_CONFIG.greetingTopics);
  const name = greetingName();
  return (
    <div className="ark-chatbot__greeting">
      <div className="ark-chatbot__greeting-hi">
        {name ? `Hi ${name}!` : 'Hi there!'}{' '}
        <span className="ark-chatbot__wave" aria-hidden="true">
          👋
        </span>
      </div>
      <div className="ark-chatbot__greeting-line">I can help you with</div>
      {CHATBOT_CONFIG.greetingTopics.length > 0 && (
        <div className="ark-chatbot__greeting-topic" aria-live="off">
          <span className="ark-chatbot__gradient-text">{topic}</span>
          <span className="ark-chatbot__caret" aria-hidden="true" />
        </div>
      )}

      {CHATBOT_CONFIG.suggestions.length > 0 && (
        <div className="ark-chatbot__suggestions">
          <div className="ark-chatbot__suggestions-label">Try asking</div>
          {CHATBOT_CONFIG.suggestions.map((s, i) => (
            <button
              key={s}
              type="button"
              className="ark-chatbot__suggestion"
              style={{ animationDelay: `${0.15 + i * 0.08}s` }}
              disabled={disabled}
              onClick={() => onPick(s)}
            >
              <span className="ark-chatbot__suggestion-icon">
                <SparkleIcon />
              </span>
              <span className="ark-chatbot__suggestion-text">{s}</span>
              <span className="ark-chatbot__suggestion-arrow" aria-hidden="true">
                →
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const createSocket = (): Socket =>
  io(CHATBOT_CONFIG.socketUrl, {
    path: CHATBOT_CONFIG.socketPath,
    // WebSocket only, no HTTP long-polling.
    transports: ['websocket'],
    autoConnect: false,
    // Own connection per widget, so resetting it never affects anything else.
    forceNew: true
  });

// Emit the question and resolve with the next reply event. One question is in
// flight at a time, so the next reply belongs to it.
const askBot = (socket: Socket, payload: unknown, signal: AbortSignal) =>
  new Promise<any>((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      socket.off(CHATBOT_CONFIG.replyEvent, onReply);
      socket.off('connect_error', onUnavailable);
      socket.off('disconnect', onUnavailable);
      signal.removeEventListener('abort', onAbort);
    };
    const onReply = (data: any) => {
      cleanup();
      resolve(data);
    };
    const onUnavailable = () => {
      cleanup();
      reject(new Error('unavailable'));
    };
    const onAbort = () => {
      cleanup();
      reject(new Error('aborted'));
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('timeout'));
    }, CHATBOT_CONFIG.timeoutMs);

    socket.on(CHATBOT_CONFIG.replyEvent, onReply);
    socket.on('connect_error', onUnavailable);
    socket.on('disconnect', onUnavailable);
    signal.addEventListener('abort', onAbort);

    // `active` (not `connected`): a second connect() while the first is still
    // pending sends a duplicate CONNECT, which the server rejects.
    if (!socket.active) socket.connect();
    // Buffered by socket.io until the connection is up.
    socket.emit(CHATBOT_CONFIG.sendEvent, payload);
  });

const INPUT_MAX_HEIGHT = 120;

const ArkChatbot = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  // ✕ asks for confirmation (bottom sheet) before ending the conversation.
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const [conversation, setConversation] =
    useState<StoredConversation>(loadConversation);
  const [input, setInput] = useState('');
  const [waiting, setWaiting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Whether the list should follow new content (user hasn't scrolled up to read).
  const followRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { messages, sessionId } = conversation;
  const lastSender = messages[messages.length - 1]?.sender;
  const hasAsked = messages.some((m) => m.sender === 'user');

  const onListScroll = () => {
    const list = listRef.current;
    if (!list) return;
    const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
    followRef.current = distance < 60;
    setShowJump(distance > 200);
  };

  const jumpToLatest = () => {
    const list = listRef.current;
    if (!list) return;
    followRef.current = true;
    list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
  };

  const copyMessage = async (m: ChatMessage) => {
    try {
      await navigator.clipboard.writeText(m.text);
      setCopiedId(m.id);
      window.setTimeout(
        () => setCopiedId((id) => (id === m.id ? null : id)),
        1600
      );
    } catch {
      // Clipboard blocked (e.g. insecure context) — nothing to do.
    }
  };

  // Scroll to the bottom when the chat opens, when the user sends a question
  // (so "Thinking…" shows) and when the reply arrives, unless the user
  // scrolled up to read earlier messages.
  useLayoutEffect(() => {
    if (!open) return;
    if (lastSender === 'user' || followRef.current) {
      followRef.current = true;
      setShowJump(false);
      const list = listRef.current;
      if (list) list.scrollTop = list.scrollHeight;
    }
  }, [open, messages.length, waiting, lastSender]);

  // Grow the input with its content, up to INPUT_MAX_HEIGHT.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, INPUT_MAX_HEIGHT)}px`;
  }, [input, open]);

  // Back to the input once the answer is in.
  useEffect(() => {
    if (open && !waiting) inputRef.current?.focus();
  }, [open, waiting]);

  const getSocket = () => {
    if (!socketRef.current) socketRef.current = createSocket();
    return socketRef.current;
  };

  // Drop the connection (and any buffered question), so a late reply to a
  // cancelled or failed question can never show up as the answer to the next one.
  const resetSocket = () => {
    socketRef.current?.disconnect();
    socketRef.current = null;
  };

  // Connect as soon as the panel opens so the first question isn't slowed down.
  useEffect(() => {
    if (open && !getSocket().active) getSocket().connect();
  }, [open]);

  useEffect(() => {
    if (!CHATBOT_CONFIG.persist) return;
    try {
      localStorage.setItem(storageKey(), JSON.stringify(conversation));
    } catch {
      // Quota exceeded / storage blocked — chat still works in memory.
    }
  }, [conversation]);

  // Cancel an in-flight question and close the socket if the widget unmounts (e.g. logout).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      socketRef.current?.disconnect();
    },
    []
  );

  const appendMessages = (sid: string, next: ChatMessage[]) =>
    setConversation((prev) =>
      // Drop replies that belong to a conversation the user already restarted.
      prev.sessionId === sid
        ? { ...prev, messages: [...prev.messages, ...next] }
        : prev
    );

  const sendMessage = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!text || waiting) return;

      const sid = sessionId;
      const history: ChatHistoryItem[] = messages
        .filter((m) => !m.isError && m.text)
        .map((m) => ({ role: m.sender, text: m.text }));

      setInput('');
      appendMessages(sid, [
        { id: newId(), sender: 'user', text, time: Date.now() }
      ]);
      setWaiting(true);

      const controller = new AbortController();
      abortRef.current = controller;
      const payload = buildRequest(text, { sessionId: sid, history });

      try {
        const data = await askBot(getSocket(), payload, controller.signal);

        const replies = parseResponse(data);
        const now = Date.now();
        appendMessages(
          sid,
          replies.length
            ? replies.map((r) => ({
                id: newId(),
                sender: 'bot' as const,
                text: r.text,
                buttons: r.buttons,
                time: now
              }))
            : [
                {
                  id: newId(),
                  sender: 'bot',
                  text: CHATBOT_CONFIG.errorMessages.noAnswer,
                  time: now,
                  isError: true
                }
              ]
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        resetSocket();
        appendMessages(sid, [
          {
            id: newId(),
            sender: 'bot',
            text: errorMessageFor(err),
            time: Date.now(),
            isError: true
          }
        ]);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setWaiting(false);
        }
      }
    },
    [messages, sessionId, waiting]
  );

  // Enter sends, Shift+Enter adds a new line.
  const onInputKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // Minimize keeps the conversation, close ends it.
  const endChat = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    // Close this conversation's socket; the next chat opens a new session.
    resetSocket();
    setWaiting(false);
    setInput('');
    setConversation(freshConversation());
    setConfirmingEnd(false);
    setOpen(false);
  };

  const minimize = () => {
    setConfirmingEnd(false);
    setOpen(false);
  };

  // Esc dismisses the end-chat confirmation.
  useEffect(() => {
    if (!confirmingEnd) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmingEnd(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmingEnd]);

  // Quick replies are only clickable on the latest bot message.
  const lastMessageId = messages[messages.length - 1]?.id;

  const renderButtons = (buttons: string[]) => (
    <div className="ark-chatbot__buttons">
      {buttons.map((label) => (
        <button
          key={label}
          type="button"
          className="ark-chatbot__button"
          disabled={waiting}
          onClick={() => sendMessage(label)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const canSend = !waiting && !!input.trim();

  // The widget's palette comes from the app's MUI theme (see ArkChatbot.css);
  // CHATBOT_CONFIG.primaryColor, when set, overrides the theme's primary.
  const { palette } = theme;
  const themeVars = {
    '--ark-chat-primary': CHATBOT_CONFIG.primaryColor || palette.primary.main,
    '--ark-chat-primary-2': palette.primary.light,
    '--ark-chat-accent': palette.primary.light,
    '--ark-chat-navy': palette.common.black,
    '--ark-chat-text': palette.text.primary,
    '--ark-chat-muted': palette.text.secondary,
    '--ark-chat-success': palette.success.main,
    '--ark-chat-danger': palette.error.main
  } as CSSProperties;

  return (
    <div
      className="ark-chatbot"
      style={themeVars}
    >
      {open && (
        <div
          className="ark-chatbot__panel"
          role="dialog"
          aria-label={CHATBOT_CONFIG.headerTitle}
        >
          {/* Slowly drifting blurred colour orbs behind everything */}
          <div className="ark-chatbot__orbs" aria-hidden="true">
            <span className="ark-chatbot__orb ark-chatbot__orb--1" />
            <span className="ark-chatbot__orb ark-chatbot__orb--2" />
            <span className="ark-chatbot__orb ark-chatbot__orb--3" />
          </div>

          <div className="ark-chatbot__header">
            <span className="ark-chatbot__header-avatar">
              <Avatar size="md" />
              {CHATBOT_CONFIG.statusText && (
                <span className="ark-chatbot__status-dot" aria-hidden="true" />
              )}
            </span>
            <div className="ark-chatbot__heading">
              <div className="ark-chatbot__title">
                {CHATBOT_CONFIG.headerTitle}
              </div>
              <div className="ark-chatbot__subtitle">
                {CHATBOT_CONFIG.statusText && (
                  <span className="ark-chatbot__status">
                    {CHATBOT_CONFIG.statusText}
                  </span>
                )}
                {CHATBOT_CONFIG.statusText && CHATBOT_CONFIG.headerSubtitle && (
                  <span className="ark-chatbot__subtitle-sep">·</span>
                )}
                {CHATBOT_CONFIG.headerSubtitle}
              </div>
            </div>
            <Tooltip title="Minimize">
              <IconButton
                size="small"
                className="ark-chatbot__header-btn"
                onClick={minimize}
                aria-label="Minimize chat"
              >
                <KeyboardArrowDownRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="End chat">
              <IconButton
                size="small"
                className="ark-chatbot__header-btn"
                onClick={() => setConfirmingEnd(true)}
                aria-label="End chat"
              >
                <CloseRoundedIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div
            className="ark-chatbot__messages"
            ref={listRef}
            onScroll={onListScroll}
            aria-live="polite"
          >
            {!hasAsked && (
              <Greeting onPick={sendMessage} disabled={waiting} />
            )}

            {messages.map((m) => {
              const isBot = m.sender === 'bot';
              return (
                <div
                  key={m.id}
                  className={`ark-chatbot__msg ark-chatbot__msg--${m.sender}${
                    m.isError ? ' ark-chatbot__msg--error' : ''
                  }`}
                >
                  <div className="ark-chatbot__msg-body">
                    <div className="ark-chatbot__meta">
                      {isBot ? CHATBOT_CONFIG.title : 'You'} at{' '}
                      {formatMeta(m.time)}
                    </div>
                    <div className="ark-chatbot__bubble">
                      {isBot ? (
                        <div
                          className="ark-chatbot__markdown"
                          dangerouslySetInnerHTML={{
                            __html: renderMarkdown(m.text)
                          }}
                        />
                      ) : (
                        <div className="ark-chatbot__user-text">{m.text}</div>
                      )}
                    </div>
                    {isBot && !m.isError && m.text && (
                      <div className="ark-chatbot__actions">
                        <button
                          type="button"
                          className="ark-chatbot__action"
                          onClick={() => copyMessage(m)}
                          aria-label="Copy reply"
                        >
                          {copiedId === m.id ? (
                            <>
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M5 12.5l4.5 4.5L19 7.5" />
                              </svg>
                              Copied
                            </>
                          ) : (
                            <>
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
                                <path d="M15.5 8.5V6.5a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2" />
                              </svg>
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    {isBot && m.buttons?.length && m.id === lastMessageId
                      ? renderButtons(m.buttons)
                      : null}
                  </div>
                </div>
              );
            })}

            {waiting && (
              <div
                className="ark-chatbot__msg ark-chatbot__msg--bot ark-chatbot__msg--thinking"
                aria-label={`${CHATBOT_CONFIG.title} is thinking`}
              >
                <div className="ark-chatbot__msg-body">
                  <div className="ark-chatbot__thinking">
                    <span className="ark-chatbot__dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                    <span className="ark-chatbot__shimmer">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {showJump && (
            <button
              type="button"
              className="ark-chatbot__jump"
              onClick={jumpToLatest}
              aria-label="Jump to latest message"
            >
              <KeyboardArrowDownRoundedIcon />
            </button>
          )}

          <div className="ark-chatbot__composer">
            <div className="ark-chatbot__input-row">
              <textarea
                ref={inputRef}
                className="ark-chatbot__input"
                rows={1}
                value={input}
                placeholder={
                  waiting
                    ? CHATBOT_CONFIG.waitingPlaceholder
                    : CHATBOT_CONFIG.placeholder
                }
                disabled={waiting}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onInputKeyDown}
                aria-label="Message"
                autoFocus
              />
              <button
                type="button"
                className="ark-chatbot__send"
                disabled={!canSend}
                onClick={() => sendMessage(input)}
                aria-label="Send message"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M21.5 2.5 10.2 13.8M21.5 2.5l-7.2 19-4.1-7.7-7.7-4.1 19-7.2Z" />
                </svg>
              </button>
            </div>
            {CHATBOT_CONFIG.footerText && (
              <div className="ark-chatbot__footer">
                {CHATBOT_CONFIG.footerText}
              </div>
            )}
          </div>

          {confirmingEnd && (
            <div
              className="ark-chatbot__confirm-backdrop"
              onClick={() => setConfirmingEnd(false)}
            >
              <div
                className="ark-chatbot__confirm"
                role="alertdialog"
                aria-label="End this chat?"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="ark-chatbot__confirm-end"
                  onClick={endChat}
                  autoFocus
                >
                  End Chat
                </button>
                <button
                  type="button"
                  className="ark-chatbot__confirm-cancel"
                  onClick={() => setConfirmingEnd(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* The open panel takes the launcher's place. */}
      {!open && (
        <button
          type="button"
          className="ark-chatbot__launcher"
          onClick={() => {
            followRef.current = true;
            setOpen(true);
          }}
          aria-label={`Open ${CHATBOT_CONFIG.title} chat`}
        >
          <svg
            className="ark-chatbot__launcher-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M12 3C6.9 3 3 6.6 3 11c0 2.1.9 4 2.4 5.4l-.9 4.1c-.1.5.4.9.8.6l4-2.1c.9.2 1.8.3 2.7.3 5.1 0 9-3.6 9-8s-3.9-8.3-9-8.3z" />
            <rect className="ark-chatbot__launcher-line" x="8" y="9" width="8" height="1.8" rx="0.9" />
            <rect className="ark-chatbot__launcher-line" x="8" y="12.4" width="5" height="1.8" rx="0.9" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ArkChatbot;
